import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange, logout as authLogout } from '../services/firebase/authService';
import { getUserProfile, isAdminRole } from '../services/firebase/usersService';
import type { ResolvedUserProfile } from '../services/firebase/usersService';
import { isEnvAdminEmail } from '../utils/userRole';

interface AuthContextType {
  user: User | null;
  profile: ResolvedUserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  refreshProfile: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
  refreshProfile: async () => undefined,
  logout: async () => undefined,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
return context;
};

export const useUserProfile = () => {
  const { profile, refreshProfile, isAdmin } = useAuth();
  
return { profile, refreshProfile, isAdmin };
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ResolvedUserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (firebaseUser: User | null) => {
    if (!firebaseUser) {
      setProfile(null);
      
return;
    }
    const p = await getUserProfile(firebaseUser.uid, firebaseUser.email);
    if (p) {
      setProfile(p);
    } else {
      const email = firebaseUser.email || '';
      setProfile({
        uid: firebaseUser.uid,
        name: firebaseUser.displayName || '',
        email,
        role: isEnvAdminEmail(email) ? 'admin' : 'employee',
        firestoreDocId: firebaseUser.uid,
        profileSource: 'synthetic',
      });
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    await loadProfile(user);
  }, [loadProfile, user]);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      setUser(firebaseUser);
      await loadProfile(firebaseUser);
      setLoading(false);
    });
    
return () => unsubscribe();
  }, [loadProfile]);

  const logout = async () => {
    await authLogout();
    setUser(null);
    setProfile(null);
    sessionStorage.removeItem('needs2FAVerification');
    sessionStorage.removeItem('pending2FAUserId');
    sessionStorage.removeItem('2faVerified');
  };

  const isAdmin = isAdminRole(profile?.role) || isEnvAdminEmail(profile?.email || user?.email);

  return (
    <AuthContext.Provider value={{ user, profile, loading, isAdmin, refreshProfile, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
